import { create } from "zustand";
import { ListingStatus, PropertyFeature, PropertyType, Sort } from "../types/constants";
import { PROPERTY_BUILT_RANGE } from "../config/constants";
import {
  addFavorite,
  filterQueryString,
  MapBounds,
  MapPin,
  ParsedFilters,
  parseQuery,
  parseSearchParamsFromUrl,
  removeFavorite,
} from "../services/api";
import { mapConfig, loginRedirectUrl } from "../config/mapConfig";

export interface Range {
  min?: number;
  max?: number;
}

/** Base map style: street tiles (OpenStreetMap) or aerial imagery (Esri). */
export type Basemap = "map" | "satellite";

export interface SearchFormData {
  /**
   * Cities to search, OR-ed together. Several at once is the point: someone
   * looking at Driggs and Victor should not have to run two searches. Sent to
   * the API as one semicolon-joined `location` param.
   */
  location: string[];
  /** AI-parsed or app-set street address filter. */
  address: string;
  /** Subdivision filter, set by an autocomplete pick or the AI parser. */
  subdivision: string;

  price: Range;
  sqft: Range;
  acres: Range;
  yearBuilt: Range;

  beds: number;
  /** AI-only exact-bedroom cap ("2 bed condo"). No UI control; a manual beds edit clears it. */
  maxBeds?: number;
  baths: number;
  /** AI-only exact-bath cap. No UI control; a manual baths edit clears it. */
  maxBaths?: number;

  propertyTypes: PropertyType[];
  propertyFeatures: PropertyFeature[];

  status: ListingStatus;
  sort: Sort;

  /** AI-only recency filter (days). No UI control; set by natural-language search. */
  maxDaysOnMarket?: number;
  /** AI-only free-text phrases ("horse property") matched against listing text and remarks. */
  keywords: string[];
}

/** Listings fetched per page; infinite scroll appends another page. */
export const PAGE_SIZE = 50;

/** Most keyword phrases we forward; the server caps at the same number. */
const MAX_KEYWORDS = 5;

export type FitFallback = { lat: number; lng: number; zoom: number } | null;

interface SearchStore {
  searchVersion: number;
  formData: SearchFormData;
  aiQuery: string;
  /** Human-readable "interpreted as …" summary of the last AI query. */
  aiSummary: string;
  /** Non-empty when the query named a place outside the service area. */
  aiError: string;
  /** True while a programmatic fit / fly is in flight. Its moveend must not
   *  scope the list to the map area. */
  programmaticMove: boolean;
  /** When the programmatic move started (ms). A moveend long after it is
   *  treated as the user's, so a fit whose animation never completed (hidden
   *  tab, reduced motion) can't swallow the next real pan. */
  programmaticMoveAt: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  /** True while the pin set reloads (filter change, re-frame). A pan reloads
   *  only the list, so it never dims the map. */
  isLoadingPins: boolean;
  /** True when the last search request failed (distinct from zero matches). */
  searchError: boolean;
  results: any[];
  totalResults: number;
  /** True when the results on screen are an area search. Set with the
   *  results, not read off mapBounds, which changes a beat before they land. */
  resultsScoped: boolean;
  hasMore: boolean;

  // Map pins: EVERY listing matching the current filters, not just the
  // loaded list pages, so the map and the list agree.
  pins: MapPin[];
  /** Pins the server matched (listings with coordinates). -1 = unknown. */
  pinsTotal: number;
  /** Set when the next result set should re-frame the map (first load, AI
   *  query, autocomplete pick). Copied into fitRequest once pins land. */
  fitPending: boolean;
  /** Where to send the map if the fit has nothing to frame (a service-area
   *  centroid from the AI parser). */
  fitFallback: FitFallback;
  /** Monotonic token: MapView fits to the current pins whenever it changes. */
  fitRequest: number;

  // Map state
  /** Area the list is scoped to, or null for the whole filter set. Only a
   *  user pan or zoom sets it (followMap). Pins are never bounded by it. */
  mapBounds: MapBounds | null;
  mapZoom: number;

  // Shared hover state between list cards and map markers
  hoveredPropertyId: string | null;
  /** Listing whose pin was clicked; the list scrolls its card into view. */
  activePropertyId: string | null;
  // Mobile view toggle: "list" | "map"
  activeView: "list" | "map";
  // Saved/favourited listings (server-backed via the Favorite table)
  savedPropertyIds: string[];
  // Listing whose "add to list" modal is open (null = closed).
  listModalListingId: string | null;
  // Parcel-lines overlay toggle (persisted to localStorage)
  showParcels: boolean;
  // Base map style: street vs satellite (persisted to localStorage)
  basemap: Basemap;

  setField: <K extends keyof SearchFormData>(field: K, value: SearchFormData[K]) => void;

  setRange: (
    field: keyof Pick<SearchFormData, "price" | "sqft" | "acres" | "yearBuilt">,
    range: Range
  ) => void;

  /** `scoped`: the results are an area search (see resultsScoped). */
  setResults: (results: any[], total: number, scoped?: boolean) => void;
  appendResults: (results: any[], total: number) => void;
  setPins: (pins: MapPin[], total: number) => void;
  setLoading: (loading: boolean) => void;
  setLoadingMore: (loading: boolean) => void;
  setLoadingPins: (loading: boolean) => void;
  setSearchError: (failed: boolean) => void;
  setAiQuery: (query: string) => void;
  setAiSummary: (summary: string) => void;
  setAiError: (message: string) => void;
  setProgrammaticMove: (on: boolean) => void;
  /** Apply server-parsed filters onto a clean form (no manual-edit side effects). */
  applyParsedFilters: (filters: ParsedFilters) => void;
  /** Parse a natural-language query, apply filters, and run the search. */
  runAiQuery: (q: string) => Promise<void>;
  /** Apply an autocomplete city pick: filter by location and fit to results. */
  applyLocation: (location: string) => void;
  /** Apply an autocomplete subdivision pick: filter by subdivision and fit to results. */
  applySubdivision: (name: string) => void;
  /** "Clear all": every filter, the sort, the AI query and the map-area limit
   *  back to the opening state, in one search, and the filter query off the
   *  URL. Keeps the map view. */
  clearAllFilters: () => void;
  setMapZoom: (zoom: number) => void;
  /** A user pan or zoom settled here: scope the list to it once the map sits still. */
  followMap: (bounds: MapBounds) => void;
  /** The map settled after a move that was not the visitor picking an area. */
  settleMapView: (bounds: MapBounds) => void;
  /** Header "Show all": drop the map-area scope, keep the filters. */
  clearMapArea: () => void;
  submitSearch: () => void;
  setHoveredProperty: (id: string | null) => void;
  setActiveProperty: (id: string | null) => void;
  setActiveView: (view: "list" | "map") => void;
  toggleSavedProperty: (id: string) => Promise<void>;
  openListModal: (id: string) => void;
  closeListModal: () => void;
  toggleParcels: () => void;
  toggleBasemap: () => void;
}

// Shared with the listing detail map (live/wwwroot/scripts/listing-map.js);
// renaming either splits the preference.
const PARCELS_STORAGE_KEY = "tvr.showParcels";
const BASEMAP_STORAGE_KEY = "tvr.basemap";

/**
 * Initial saved listings. Favorites are server-backed (the Favorite table,
 * shared with the listing detail page and the My Account page), so we seed
 * from the IDs injected into the page config rather than from localStorage.
 * Signed-out visitors can't favorite (their hearts redirect to login), so
 * they start with an empty set.
 */
function initialSavedIds(): string[] {
  return mapConfig.isSignedIn ? [...mapConfig.favorites] : [];
}

function loadShowParcels(): boolean {
  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem(PARCELS_STORAGE_KEY) === "1"
      : false;
  } catch {
    return false;
  }
}

function persistShowParcels(on: boolean): void {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(PARCELS_STORAGE_KEY, on ? "1" : "0");
    }
  } catch {
    /* localStorage blocked: silently no-op */
  }
}

function loadBasemap(): Basemap {
  try {
    return typeof window !== "undefined" &&
      window.localStorage.getItem(BASEMAP_STORAGE_KEY) === "satellite"
      ? "satellite"
      : "map";
  } catch {
    return "map";
  }
}

function persistBasemap(value: Basemap): void {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(BASEMAP_STORAGE_KEY, value);
    }
  } catch {
    /* localStorage blocked: silently no-op */
  }
}

/**
 * Drop the query string and hash from the address bar, so a reload after
 * "Clear all" does not restore the filters of the link the page opened with.
 * replaceState, not pushState: Back still leaves the page.
 */
function clearFilterUrl(): void {
  try {
    const { pathname, search, hash } = window.location;
    if (search || hash) window.history.replaceState(window.history.state, "", pathname);
  } catch {
    /* history blocked (sandboxed frame): the filters still clear on screen */
  }
}

function cleanKeywords(raw: string[] | null | undefined): string[] {
  return (raw || [])
    .map((k) => String(k).trim())
    .filter((k) => k.length >= 2)
    .filter((k, i, arr) => arr.indexOf(k) === i)
    .slice(0, MAX_KEYWORDS);
}

/**
 * Normalise the city list from any of the shapes it arrives in: an array from
 * the store, a semicolon-joined string from a shared link or the AI parser, or
 * nothing at all. Duplicates are dropped case-insensitively so picking a city
 * that is already applied is a no-op rather than a doubled chip.
 */
export function cleanLocations(raw: string[] | string | null | undefined): string[] {
  const list = Array.isArray(raw) ? raw : String(raw ?? "").split(";");
  const seen = new Set<string>();

  return list
    .map((c) => String(c).trim())
    .filter((c) => c.length > 0)
    .filter((c) => {
      const key = c.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/**
 * True when a subdivision value means "no subdivision": blank, or a stand-in
 * the MLS feeds put in its place. The feeds carry "None" and
 * "ZZZ--VOID--Do NOT Use" today. The other exact values are the ones the No
 * Subdivision filter already treated as none, plus Spark's masked-field
 * marker. The zzz and "do not use" tests catch any later variant of the ZZZ
 * value, and the void test catches "void", "zz void", "zzvoid" and the like.
 * Compared trimmed and case-insensitively. Mirrors
 * SubdivisionNames.IsPlaceholder in live/Models/SubdivisionNames.cs, so change
 * both together. The list lives inside the function on purpose: api.ts and
 * this module import each other, and a module-level const can be read before
 * it is set.
 */
export function isPlaceholderSubdivision(name: string | null | undefined): boolean {
  const v = String(name ?? "")
    .trim()
    .toLowerCase();
  if (v === "") return true;
  const exact = [
    "none",
    "n/a",
    "na",
    "no subdivision",
    "not in subdivision",
    "not applicable",
    "other",
    "********",
  ];
  return (
    exact.includes(v) ||
    v.startsWith("zzz") ||
    v.includes("do not use") ||
    /^z*\s*void(?![a-z0-9])/.test(v)
  );
}

/** Build a clean SearchFormData from server-parsed AI filters. */
function parsedToFormData(f: ParsedFilters): SearchFormData {
  const allowedTypes = Object.values(PropertyType) as string[];
  const allowedFeatures = Object.values(PropertyFeature) as string[];
  const allowedSorts = Object.values(Sort) as string[];

  const range = (min: number | null, max: number | null): Range => {
    const r: Range = {};
    if (min != null) r.min = min;
    if (max != null) r.max = max;
    return r;
  };

  const beds = f.beds > 0 ? f.beds : 0;
  const baths = f.baths > 0 ? f.baths : 0;

  return {
    location: cleanLocations(f.location),
    address: f.address || "",
    subdivision: f.subdivision && !isPlaceholderSubdivision(f.subdivision) ? f.subdivision : "",
    price: range(f.minPrice, f.maxPrice),
    sqft: range(f.minSqft, f.maxSqft),
    acres: range(f.minAcres, f.maxAcres),
    yearBuilt:
      f.minYearBuilt != null || f.maxYearBuilt != null
        ? range(f.minYearBuilt, f.maxYearBuilt)
        : { min: PROPERTY_BUILT_RANGE.MIN, max: PROPERTY_BUILT_RANGE.MAX },
    beds,
    // A cap below the floor is contradictory; drop it rather than return nothing.
    maxBeds: f.maxBeds != null && f.maxBeds > 0 && f.maxBeds >= beds ? f.maxBeds : undefined,
    baths,
    maxBaths: f.maxBaths != null && f.maxBaths > 0 && f.maxBaths >= baths ? f.maxBaths : undefined,
    propertyTypes: (f.propertyTypes || []).filter((t) =>
      allowedTypes.includes(t)
    ) as PropertyType[],
    propertyFeatures: (f.propertyFeatures || []).filter((t) =>
      allowedFeatures.includes(t)
    ) as PropertyFeature[],
    status: ListingStatus.Active,
    sort: allowedSorts.includes(f.sort) ? (f.sort as Sort) : Sort.Newest,
    maxDaysOnMarket: f.maxDaysOnMarket && f.maxDaysOnMarket > 0 ? f.maxDaysOnMarket : undefined,
    keywords: cleanKeywords(f.keywords),
  };
}

const initialState: SearchFormData = {
  location: [],
  address: "",
  subdivision: "",

  price: {},
  sqft: {},
  acres: {},
  yearBuilt: { min: PROPERTY_BUILT_RANGE.MIN, max: PROPERTY_BUILT_RANGE.MAX },

  beds: 0,
  maxBeds: undefined,
  baths: 0,
  maxBaths: undefined,

  propertyTypes: [],
  propertyFeatures: [],

  status: ListingStatus.Active,
  sort: Sort.Newest,
  maxDaysOnMarket: undefined,
  keywords: [],
};

/** True when a filter narrows the results. Sort only orders them, so it does
 *  not count. Compared as query strings, so a value the API ignores does not
 *  count either. */
export function hasActiveFilters(f: SearchFormData): boolean {
  return filterQueryString(f) !== filterQueryString(initialState);
}

/**
 * Seed the form from structured URL params (a shared link or a saved-search
 * "Search" link) BEFORE the first render, so the very first search already
 * reflects the saved filters, with no reliance on a post-mount effect re-trigger.
 * The natural-language `?q=` path is intentionally left to the Results effect,
 * since it runs an async parse + search rather than applying static filters.
 */
function buildInitialFormData(): SearchFormData {
  if (typeof window === "undefined") return initialState;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("q")) return initialState; // AI query handled in Results
    const restored = parseSearchParamsFromUrl(params);
    return {
      ...initialState,
      ...restored,
      price: { ...initialState.price, ...(restored.price ?? {}) },
      sqft: { ...initialState.sqft, ...(restored.sqft ?? {}) },
      acres: { ...initialState.acres, ...(restored.acres ?? {}) },
      yearBuilt: { ...initialState.yearBuilt, ...(restored.yearBuilt ?? {}) },
      location: cleanLocations(restored.location),
      keywords: cleanKeywords(restored.keywords),
    };
  } catch {
    return initialState;
  }
}

/** Shared "leave AI mode" fields for manual edits. */
const exitAiMode = { aiQuery: "", aiSummary: "", aiError: "" };

/** How long the map must sit still after a user pan or zoom before the list follows it. */
const FOLLOW_MAP_DEBOUNCE_MS = 500;

/** Share of the viewport span an edge may shift and still count as the same area. */
const SAME_AREA_TOLERANCE = 0.02;

/**
 * True when two viewports are the same area give or take a few pixels, such
 * as a pin tap that slipped into a tiny drag. Searching again for that would
 * only jump the list back to the top.
 */
function nearlySameArea(a: MapBounds, b: MapBounds): boolean {
  const latTol = (a.north - a.south) * SAME_AREA_TOLERANCE;
  const lngTol = (a.east - a.west) * SAME_AREA_TOLERANCE;
  return (
    Math.abs(a.north - b.north) <= latTol &&
    Math.abs(a.south - b.south) <= latTol &&
    Math.abs(a.east - b.east) <= lngTol &&
    Math.abs(a.west - b.west) <= lngTol
  );
}

// The pending "list follows the map" refresh. A timer, not state: nothing
// renders it. Every action that re-scopes the search itself (AI query, place
// pick, clear, "Show all") cancels it, so a pan made just before one cannot
// land after it and pin the new search to a rectangle the map is about to leave.
let followTimer: number | undefined;
let followBounds: MapBounds | null = null;
// Set when the map settled again (popup autopan, resize) while a follow was
// waiting: the follow then commits that view and keeps the pin pick.
let followSettled = false;

// Bumped by every AI query and by every other change to the filters (a manual
// edit, a place pick, Clear all). A parse that comes back after one is stale:
// it must not re-apply filters the visitor has since replaced or cleared.
let aiQueryToken = 0;

// Viewports for the same-area check, also kept out of state. lastView is where
// the map last settled, whatever moved it. alignedView is the viewport the
// whole-set list last lined up with: the map at load, after a fit (or another
// move the list ignores), or when the list went back to the whole set. A user
// move on a whole-set list is measured against alignedView, not the previous
// moveend, so a run of small nudges still adds up to a follow.
let lastView: MapBounds | null = null;
let alignedView: MapBounds | null = null;

function cancelFollow(): void {
  if (followTimer !== undefined) window.clearTimeout(followTimer);
  followTimer = undefined;
  followBounds = null;
  followSettled = false;
}

/** The list is going back to the whole filter set: drop a pending pan, and
 *  line the list up with the map as it stands now. */
function unscopeList(): void {
  cancelFollow();
  alignedView = lastView;
}

/** A restored link that names a place opens framed on it. */
function hasPlaceFilter(f: SearchFormData): boolean {
  return f.location.length > 0 || !!f.subdivision || !!f.address;
}

const initialFormData = buildInitialFormData();

export const useSearchStore = create<SearchStore>((set) => ({
  searchVersion: 0,
  formData: initialFormData,
  aiQuery: "",
  aiSummary: "",
  aiError: "",
  programmaticMove: false,
  programmaticMoveAt: 0,
  isLoading: false,
  isLoadingMore: false,
  isLoadingPins: false,
  searchError: false,
  results: [],
  totalResults: 0,
  resultsScoped: false,
  hasMore: false,
  pins: [],
  pinsTotal: -1,
  // First load stays on the Teton Valley home view (MapView's TETON_VALLEY_CENTER
  // / DEFAULT_ZOOM). Framing the full result set instead opened the map on
  // Billings-to-Salt-Lake, with the biggest clusters on Idaho Falls and Rexburg,
  // because the feed covers far more ground than the brokerage markets. Every
  // deliberate re-frame (AI query, location or subdivision pick) still sets
  // fitPending itself, so only the opening view changes.
  // A shared or saved link that names a place is the exception: it opens
  // framed on that place, so the first nudge of the map can't scope the list
  // to an area holding none of its results.
  fitPending: hasPlaceFilter(initialFormData),
  fitFallback: null,
  fitRequest: 0,
  mapBounds: null,
  mapZoom: 11,
  hoveredPropertyId: null,
  activePropertyId: null,
  activeView: "list",
  savedPropertyIds: initialSavedIds(),
  listModalListingId: null,
  showParcels: loadShowParcels(),
  basemap: loadBasemap(),

  // Manual edits exit "AI mode": clear the query + summary so the displayed
  // search reflects reality and normal live-filtering resumes. A manual beds
  // or baths pick also drops the AI-only exact cap, since the dropdown shows
  // "N+" and the cap would silently contradict it. An edit made while a parse
  // is out also drops that parse (aiQueryToken).
  setField: (field, value) => {
    aiQueryToken++;
    set((state) => {
      const formData: SearchFormData = { ...state.formData, [field]: value };
      if (field === "beds") formData.maxBeds = undefined;
      if (field === "baths") formData.maxBaths = undefined;
      return { ...exitAiMode, formData };
    });
  },

  setRange: (field, range) => {
    aiQueryToken++;
    set((state) => ({
      ...exitAiMode,
      formData: {
        ...state.formData,
        [field]: {
          ...state.formData[field],
          ...range,
        },
      },
    }));
  },

  // New search: replace the list and recompute whether more pages exist.
  setResults: (results, total, scoped = false) =>
    set({
      results,
      totalResults: total,
      resultsScoped: scoped,
      hasMore: results.length < total,
    }),

  // Infinite scroll: append the next page. total < 0 means the server
  // skipped COUNT for this page: keep the page-0 total.
  appendResults: (results, total) =>
    set((state) => {
      const merged = [...state.results, ...results];
      const knownTotal = total >= 0 ? total : state.totalResults;
      return {
        results: merged,
        totalResults: knownTotal,
        hasMore: results.length > 0 && merged.length < knownTotal,
      };
    }),

  // Pins for the new search have landed. If a re-frame was requested, hand
  // it to the map now (never earlier, or it would fit the previous pins).
  setPins: (pins, total) =>
    set((state) => ({
      pins,
      pinsTotal: total,
      fitPending: false,
      fitRequest: state.fitPending ? state.fitRequest + 1 : state.fitRequest,
    })),

  setLoading: (isLoading) => set({ isLoading }),
  setLoadingMore: (isLoadingMore) => set({ isLoadingMore }),
  setLoadingPins: (isLoadingPins) => set({ isLoadingPins }),
  setSearchError: (searchError) => set({ searchError }),
  setAiQuery: (aiQuery) => set({ aiQuery }),
  setAiSummary: (aiSummary) => set({ aiSummary }),
  setAiError: (aiError) => set({ aiError }),
  setProgrammaticMove: (programmaticMove) =>
    set({ programmaticMove, programmaticMoveAt: programmaticMove ? Date.now() : 0 }),

  // Direct formData set, deliberately NOT via setField so it doesn't trip
  // the "manual edit clears AI" behaviour.
  applyParsedFilters: (filters) => set({ formData: parsedToFormData(filters) }),

  runAiQuery: async (q) => {
    const query = q.trim();
    if (!query) return;
    const token = ++aiQueryToken;
    // Drop a pan still waiting to land. Left to fire during the parse, it
    // would run a bounded search on the old filters and un-dim the map early.
    cancelFollow();
    // A stale parse leaves the loading flags to whatever replaced it: a
    // search started since, or a newer parse (which sets aiQuery). With
    // neither, this parse clears them. That happens when an edit is undone
    // inside the live-filter debounce: no search runs, and the map and list
    // would stay dimmed.
    const version = useSearchStore.getState().searchVersion;
    const releaseStale = () => {
      const now = useSearchStore.getState();
      if (now.searchVersion === version && !now.aiQuery) {
        set({ isLoading: false, isLoadingPins: false });
      }
    };
    set({ aiQuery: query, isLoading: true, isLoadingPins: true, aiError: "" });
    try {
      const { filters, summary, area, inServiceArea, rateLimited } = await parseQuery(query);

      // Cleared or replaced while the parse was out.
      if (token !== aiQueryToken) {
        releaseStale();
        return;
      }

      // Hit the abuse guard → tell the user, run nothing.
      if (rateLimited) {
        set({
          isLoading: false,
          isLoadingPins: false,
          aiSummary: "",
          aiError: "You're searching a bit fast. Give it a few seconds and try again.",
        });
        return;
      }

      // Out of service area → block: no filter apply, no search, no map move.
      if (!inServiceArea) {
        set({
          isLoading: false,
          isLoadingPins: false,
          aiSummary: "",
          aiError:
            "That area is outside our service area. We cover Teton Valley and eastern Idaho through Jackson Hole. Try Driggs, Victor, Tetonia, Idaho Falls, Rexburg, Ashton, or Jackson.",
        });
        return;
      }

      if (filters) set({ formData: parsedToFormData(filters) });
      // An AI query is scoped by its parsed filters (incl. city), not the
      // previous viewport: clear bounds so an area search isn't zeroed out
      // by a stale map rectangle, and drop a pan made during the parse. The
      // map then re-frames to whatever the search returns, falling back to
      // the named area's centroid when it returns nothing.
      unscopeList();
      set({
        aiSummary: summary || "",
        mapBounds: null,
        fitPending: true,
        fitFallback: area ? { lat: area.lat, lng: area.lng, zoom: area.zoom } : null,
      });
    } catch (err) {
      if (token !== aiQueryToken) {
        releaseStale();
        return;
      }
      console.error("AI query failed:", err);
      set({ aiSummary: "" });
    }
    // Run the normal structured search with the applied filters.
    set((state) => ({ searchVersion: state.searchVersion + 1 }));
  },

  // Autocomplete city pick: add to the city filter, drop any
  // subdivision/address. Adding rather than replacing is what lets someone
  // search Driggs and Victor together; picking one already applied is a no-op.
  // The box is cleared rather than showing the pick, because the next thing
  // typed should be able to find another city to add.
  applyLocation: (location) => {
    aiQueryToken++;
    unscopeList();
    set((state) => ({
      formData: {
        ...state.formData,
        location: cleanLocations([...state.formData.location, location]),
        subdivision: "",
        address: "",
      },
      aiQuery: "",
      aiSummary: "",
      aiError: "",
      mapBounds: null,
      fitPending: true,
      fitFallback: null,
      searchVersion: state.searchVersion + 1,
    }));
  },

  // Autocomplete subdivision pick: filter by subdivision, drop location/address.
  // A feed stand-in ("None") is not a place to filter by, so it is ignored.
  applySubdivision: (name) => {
    if (isPlaceholderSubdivision(name)) return;
    aiQueryToken++;
    unscopeList();
    set((state) => ({
      formData: { ...state.formData, subdivision: name, location: [], address: "" },
      aiQuery: name,
      aiSummary: "",
      aiError: "",
      mapBounds: null,
      fitPending: true,
      fitFallback: null,
      searchVersion: state.searchVersion + 1,
    }));
  },

  // "Clear all" (the x in the search box, or Clear filters): every filter,
  // the sort, the AI query and the map-area limit back to the opening state,
  // in one search. It resets to the blank state, not to the filters of a link
  // the page opened with, and takes that link's query off the URL so a reload
  // stays clear.
  clearAllFilters: () => {
    aiQueryToken++;
    unscopeList();
    clearFilterUrl();
    set((state) => ({
      ...exitAiMode,
      formData: initialState,
      mapBounds: null,
      // Leave the map where the visitor left it. Re-framing here would fit the
      // now-unfiltered pin set and yank them back out to the full multi-state
      // extent, which is the same view the opening screen deliberately avoids.
      // The next pan scopes the list again.
      fitPending: false,
      fitFallback: null,
      searchVersion: state.searchVersion + 1,
    }));
  },

  setMapZoom: (mapZoom) => set({ mapZoom }),

  // A user pan or zoom (MapBoundsTracker). Waits for the map to sit still,
  // then scopes the list to the viewport and re-runs it. A move that barely
  // shifts the view is ignored: measured against the list's own area, or for
  // a whole-set list against the view it last lined up with.
  followMap: (bounds) => {
    cancelFollow();
    lastView = bounds;
    const { mapBounds, fitPending } = useSearchStore.getState();
    const reference = mapBounds ?? alignedView;
    if (reference && nearlySameArea(reference, bounds)) return;

    // The visitor has taken the map, so a re-frame still waiting on pins is
    // dropped rather than yanking the view out from under them. Dropped now,
    // not when the timer fires: otherwise the pins could land first, the map
    // would fit, and the timer would then scope the list to the view the fit
    // replaced.
    if (fitPending) set({ fitPending: false, fitFallback: null });

    followBounds = bounds;
    followTimer = window.setTimeout(() => {
      // Read at fire time: settleMapView may have moved the target since.
      const area = followBounds ?? bounds;
      const settled = followSettled;
      followTimer = undefined;
      followBounds = null;
      followSettled = false;
      set((state) => ({
        mapBounds: area,
        // The refresh jumps the list to the top. Clearing the pin pick lets a
        // second click on the same pin scroll its card back into view, unless
        // that pick is what just autopanned the map.
        activePropertyId: settled ? state.activePropertyId : null,
        searchVersion: state.searchVersion + 1,
      }));
    }, FOLLOW_MAP_DEBOUNCE_MS);
  },

  // A move that was not the visitor picking an area (load, a fit, a popup
  // autopan, a resize of a whole-set list). The list keeps its results, and a
  // whole-set list now lines up with this viewport. A follow still waiting
  // would commit a view the map has already left, so it takes this one.
  settleMapView: (bounds) => {
    lastView = bounds;
    if (followTimer !== undefined) {
      followBounds = bounds;
      followSettled = true;
      return;
    }
    if (!useSearchStore.getState().mapBounds) alignedView = bounds;
  },

  // Header "Show all": back to the whole filter set without touching the
  // filters or the map. The next pan scopes the list again.
  clearMapArea: () => {
    unscopeList();
    set((state) => ({ mapBounds: null, searchVersion: state.searchVersion + 1 }));
  },

  // Re-run the search. A pan still inside its debounce is committed now, so a
  // filter edit never lists results for a rectangle the map no longer shows,
  // and the pan does not cost a second search a moment later.
  submitSearch: () => {
    const pending = followBounds;
    cancelFollow();
    set((state) => ({
      mapBounds: pending ?? state.mapBounds,
      searchVersion: state.searchVersion + 1,
    }));
  },

  setHoveredProperty: (hoveredPropertyId) => set({ hoveredPropertyId }),

  setActiveProperty: (activePropertyId) => set({ activePropertyId }),

  setActiveView: (activeView) => set({ activeView }),

  // Server-backed favorite toggle. Updates the UI optimistically, calls the
  // ListingsApi, and rolls back if the request fails. A session that expired
  // since page load surfaces as 401 → send the visitor to log in. (Signed-out
  // clicks are intercepted in PropertyCard before they reach the store.)
  toggleSavedProperty: async (id) => {
    if (!id) return;

    const wasSaved = useSearchStore.getState().savedPropertyIds.includes(id);
    const optimistic = wasSaved
      ? useSearchStore.getState().savedPropertyIds.filter((x) => x !== id)
      : [...useSearchStore.getState().savedPropertyIds, id];
    set({ savedPropertyIds: optimistic });

    const result = wasSaved ? await removeFavorite(id) : await addFavorite(id);

    if (result.status === "ok") return;

    if (result.status === "unauthenticated") {
      const login = loginRedirectUrl();
      if (login) window.location.href = login;
      return;
    }

    // Error: roll back to the pre-click state (guarding against races with
    // other toggles by recomputing from current state).
    set((state) => {
      const ids = state.savedPropertyIds.filter((x) => x !== id);
      return { savedPropertyIds: wasSaved ? [...ids, id] : ids };
    });
    window.alert("Sorry, we couldn't update your favorites. Please try again.");
  },

  openListModal: (id) => set({ listModalListingId: id }),
  closeListModal: () => set({ listModalListingId: null }),

  toggleParcels: () =>
    set((state) => {
      const next = !state.showParcels;
      persistShowParcels(next);
      return { showParcels: next };
    }),

  toggleBasemap: () =>
    set((state) => {
      const next: Basemap = state.basemap === "satellite" ? "map" : "satellite";
      persistBasemap(next);
      return { basemap: next };
    }),
}));
